import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * PixelToast
 * Retro notification pop-up system.
 *
 * Two exports:
 *   1. PixelToastContainer — place once in App.js (renders all toasts)
 *   2. useToast — hook to trigger toasts from anywhere
 *
 * Usage:
 *   const { showToast } = useToast();
 *   showToast({ type: "success", message: "تکلیف ارسال شد!" });
 *
 * Toast types: "success" | "error" | "warning" | "info" | "xp"
 */

// ── Global toast store (simple pub-sub without Redux) ──────────────────
let toastListeners = [];
let toastId = 0;

const toastStore = {
  subscribe: (listener) => {
    toastListeners.push(listener);
    return () => {
      toastListeners = toastListeners.filter((l) => l !== listener);
    };
  },
  emit: (toast) => {
    toastListeners.forEach((l) => l(toast));
  },
};

// ── Hook ───────────────────────────────────────────────────────────────
export const useToast = () => {
  const showToast = useCallback(
    ({ type = "info", message, duration = 3000, icon }) => {
      toastStore.emit({ id: ++toastId, type, message, duration, icon });
    },
    []
  );

  return { showToast };
};

// ── Individual Toast Item ──────────────────────────────────────────────
const TOAST_CONFIG = {
  success: { icon: "✅", label: "موفق" },
  error:   { icon: "❌", label: "خطا" },
  warning: { icon: "⚠️", label: "هشدار" },
  info:    { icon: "ℹ️", label: "اطلاع" },
  xp:      { icon: "⚡", label: "XP" },
};

const ToastItem = ({ toast, onRemove }) => {
  const [exiting, setExiting] = useState(false);
  const config = TOAST_CONFIG[toast.type] || TOAST_CONFIG.info;

  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onRemove(toast.id), 300);
    }, toast.duration);

    return () => clearTimeout(timer);
  }, [toast, onRemove]);

  return (
    <div
      className={`pixel-toast pixel-toast--${toast.type} ${exiting ? "pixel-toast--exit" : "pixel-toast--enter"}`}
      role="alert"
      aria-live="polite"
    >
      <span className="pixel-toast__icon">{toast.icon || config.icon}</span>
      <span className="pixel-toast__message">{toast.message}</span>
      <button
        className="pixel-toast__close"
        onClick={() => {
          setExiting(true);
          setTimeout(() => onRemove(toast.id), 300);
        }}
        aria-label="بستن"
      >
        ✕
      </button>
    </div>
  );
};

// ── Container — place once in App.js ─────────────────────────────────
export const PixelToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsubscribe = toastStore.subscribe((toast) => {
      setToasts((prev) => [...prev.slice(-4), toast]);
    });
    return unsubscribe;
  }, []);

  const handleRemove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return createPortal(
    <div className="pixel-toast-container" aria-label="اعلان‌ها">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={handleRemove} />
      ))}
    </div>,
    document.body
  );
};

export default PixelToastContainer;

/*
─── CSS to add in pixel.css / animations.css ──────────────────────────

*/